import React from 'react';
import { Card, Tag, Tooltip, Space, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Greek keyboard mapping (basic characters)
const greekKeyboard = {
    row1: [
        { key: 'q', char: 'θ', name: 'theta' },
        { key: 'w', char: 'ω', name: 'omega' },
        { key: 'e', char: 'ε', name: 'epsilon' },
        { key: 'r', char: 'ρ', name: 'rho' },
        { key: 't', char: 'τ', name: 'tau' },
        { key: 'y', char: 'ψ', name: 'psi' },
        { key: 'u', char: 'υ', name: 'upsilon' },
        { key: 'i', char: 'ι', name: 'iota' },
        { key: 'o', char: 'ο', name: 'omicron' },
        { key: 'p', char: 'π', name: 'pi' },
    ],
    row2: [
        { key: 'a', char: 'α', name: 'alpha' },
        { key: 's', char: 'σ', name: 'sigma' },
        { key: 'd', char: 'δ', name: 'delta' },
        { key: 'f', char: 'φ', name: 'phi' },
        { key: 'g', char: 'γ', name: 'gamma' },
        { key: 'h', char: 'η', name: 'eta' },
        { key: 'j', char: '—', name: '(unused)' },
        { key: 'k', char: 'κ', name: 'kappa' },
        { key: 'l', char: 'λ', name: 'lambda' },
    ],
    row3: [
        { key: 'z', char: 'ζ', name: 'zeta' },
        { key: 'x', char: 'ξ', name: 'xi' },
        { key: 'c', char: 'χ', name: 'chi' },
        { key: 'v', char: '—', name: '(unused)' },
        { key: 'b', char: 'β', name: 'beta' },
        { key: 'n', char: 'ν', name: 'nu' },
        { key: 'm', char: 'μ', name: 'mu' },
    ],
};

// Greek modifiers/accents
const greekModifiers = [
    { combo: "' + vowel", result: 'ά έ ί ό ύ ώ ή', desc: 'Acute accent' },
    { combo: '; + vowel', result: 'ὰ ὲ ὶ ὸ ὺ ὼ ὴ', desc: 'Grave accent' },
    { combo: ': + vowel', result: 'ᾶ ῖ ῦ ῆ ῶ', desc: 'Circumflex' },
    { combo: 'j + vowel', result: 'ἀ ἐ ἰ ὀ ὐ', desc: 'Smooth breathing' },
    { combo: 'J + vowel', result: 'ἁ ἑ ἱ ὁ ὑ', desc: 'Rough breathing' },
    { combo: '[ + vowel', result: 'ἄ ἔ ἴ ὄ ὔ ἤ', desc: 'Smooth + acute' },
    { combo: '{ + vowel', result: 'ἅ ἕ ἵ ὅ ὕ ἥ', desc: 'Rough + acute' },
    { combo: '/ + a/h', result: 'ᾳ ῃ', desc: 'Iota subscript' },
];

// Greek capital letters (Shift + key)
const greekCapitals = [
    { key: 'A', char: 'Α' }, { key: 'B', char: 'Β' }, { key: 'C', char: 'Χ' },
    { key: 'D', char: 'Δ' }, { key: 'E', char: 'Ε' }, { key: 'F', char: 'Φ' },
    { key: 'G', char: 'Γ' }, { key: 'H', char: 'Η' }, { key: 'I', char: 'Ι' },
    { key: 'K', char: 'Κ' }, { key: 'L', char: 'Λ' }, { key: 'M', char: 'Μ' },
    { key: 'N', char: 'Ν' }, { key: 'O', char: 'Ο' }, { key: 'P', char: 'Π' },
    { key: 'Q', char: 'Θ' }, { key: 'R', char: 'Ρ' }, { key: 'S', char: 'Σ' },
    { key: 'T', char: 'Τ' }, { key: 'U', char: 'Υ' }, { key: 'V', char: 'ς', name: 'final sigma' },
    { key: 'W', char: 'Ω' }, { key: 'X', char: 'Ξ' }, { key: 'Y', char: 'Ψ' },
    { key: 'Z', char: 'Ζ' },
];

// Hebrew keyboard mapping (basic characters)
const hebrewKeyboard = {
    row1: [
        { key: 'q', char: 'ק', name: 'qof' },
        { key: 'w', char: 'ו', name: 'vav' },
        { key: 'e', char: 'ֶ', name: 'segol' },
        { key: 'r', char: 'ר', name: 'resh' },
        { key: 't', char: 'ת', name: 'tav' },
        { key: 'y', char: 'י', name: 'yod' },
        { key: 'u', char: 'ֻ', name: 'qubuts' },
        { key: 'i', char: 'ִ', name: 'hiriq' },
        { key: 'o', char: 'ֹ', name: 'holam' },
        { key: 'p', char: 'פ', name: 'pe' },
    ],
    row2: [
        { key: 'a', char: 'ַ', name: 'patah' },
        { key: 's', char: 'ס', name: 'samekh' },
        { key: 'd', char: 'ד', name: 'dalet' },
        { key: 'f', char: 'שׂ', name: 'sin' },
        { key: 'g', char: 'ג', name: 'gimel' },
        { key: 'h', char: 'ה', name: 'he' },
        { key: 'j', char: 'שׁ', name: 'shin' },
        { key: 'k', char: 'כ', name: 'kaf' },
        { key: 'l', char: 'ל', name: 'lamed' },
    ],
    row3: [
        { key: 'z', char: 'ז', name: 'zayin' },
        { key: 'x', char: 'ח', name: 'het' },
        { key: 'c', char: 'צ', name: 'tsadi' },
        { key: 'v', char: 'ט', name: 'tet' },
        { key: 'b', char: 'ב', name: 'bet' },
        { key: 'n', char: 'נ', name: 'nun' },
        { key: 'm', char: 'מ', name: 'mem' },
    ],
};

// Hebrew special keys and modifiers
const hebrewSpecialKeys = [
    { key: ';', char: 'ְ', name: 'sheva' },
    { key: '=', char: 'ּ', name: 'dagesh' },
    { key: '-', char: '־', name: 'maqaf' },
    { key: ',', char: 'ע', name: 'ayin' },
    { key: '.', char: 'א', name: 'alef' },
];

// Hebrew Shift state - finals, vowels, and some cantillation
const hebrewShift = [
    { combo: 'K', result: 'ך', desc: 'final kaf' },
    { combo: 'M', result: 'ם', desc: 'final mem' },
    { combo: 'N', result: 'ן', desc: 'final nun' },
    { combo: 'P', result: 'ף', desc: 'final pe' },
    { combo: 'C', result: 'ץ', desc: 'final tsadi' },
    { combo: 'A', result: 'ָ', desc: 'qamats' },
    { combo: 'E', result: 'ֵ', desc: 'tsere' },
    { combo: 'S', result: 'ש', desc: 'shin (no dot)' },
    { combo: 'Q', result: '\u0597', desc: 'revia' },
    { combo: 'Y', result: '\u059F', desc: 'qarney para' },
];

// Hebrew number keys (cantillation marks)
const hebrewNumbers = [
    { combo: '2', result: '\u0598', desc: 'zarqa' },
    { combo: '3', result: '\u05A8', desc: 'qadma' },
    { combo: '4', result: '\u059C', desc: 'geresh' },
    { combo: '5', result: '\u059E', desc: 'gershayim' },
    { combo: '7', result: '\u05AC', desc: 'iluy' },
    { combo: '8', result: '\u059D', desc: 'geresh muqdam' },
];

// Hebrew AltGr state (Alt+Ctrl) - $X patterns (type $ then the key)
const hebrewAltGr = [
    { combo: '$1', result: '\u05BD', desc: 'meteg' },
    { combo: '$3', result: '\u0596', desc: 'tipeha' },
    { combo: '$4', result: '\u05A5', desc: 'merkha' },
    { combo: '$5', result: '\u05A6', desc: 'merkha kefula' },
    { combo: '$6', result: '\u05AD', desc: 'dehi' },
    { combo: '$7', result: '\u05A3', desc: 'munah' },
    { combo: '$8', result: '\u059B', desc: 'tevir' },
    { combo: '$9', result: '\u05A7', desc: 'darga' },
    { combo: '$0', result: '\u05AA', desc: 'yerah ben yomo' },
    { combo: '$=', result: '\u0591', desc: 'etnahta' },
    { combo: '$\\', result: '\u05A4', desc: 'mahapakh' },
    { combo: '$]', result: '\u059A', desc: 'yetiv' },
    { combo: '$;', result: ';', desc: 'semicolon' },
    { combo: '$/', result: '׳', desc: 'geresh (punctuation)' },
];

// Hebrew AltGr+Shift state (Alt+Ctrl+Shift) - @X patterns (type @ then the key)
const hebrewAltGrShift = [
    { combo: '@2', result: '\u05AE', desc: 'zinor' },
    { combo: '@3', result: '\u0599', desc: 'pashta' },
    { combo: '@4', result: '\u05A0', desc: 'telisha gedola' },
    { combo: '@5', result: '\u05A9', desc: 'telisha qetana' },
    { combo: '@7', result: '\u05A1', desc: 'pazer' },
    { combo: '@8', result: '\u0595', desc: 'zaqef gadol' },
    { combo: '@9', result: '\u0593', desc: 'shalshelet' },
    { combo: '@0', result: '\u05AF', desc: 'masora circle' },
    { combo: '@-', result: '\u05BF', desc: 'rafe' },
    { combo: '@\\', result: '\u05AB', desc: 'ole' },
    { combo: '@E', result: '\u05B1', desc: 'hataf segol' },
    { combo: '@A', result: '\u05B2', desc: 'hataf patah' },
    { combo: '@O', result: '\u05B3', desc: 'hataf qamats' },
    { combo: '@[', result: '\u0594', desc: 'zaqef qatan' },
    { combo: '@]', result: '\u0592', desc: 'segolta' },
    { combo: '@;', result: '׃', desc: 'sof pasuq' },
    { combo: "@'", result: '\u05C4', desc: 'punctum' },
];

interface KeyboardReferenceProps {
    lang: string;
    onClose?: () => void;
    onCharacterClick?: (char: string) => void;
}

const KeyButton: React.FC<{
    keyInfo: { key: string; char: string; name: string };
    lang: string;
    onClick?: (char: string) => void;
}> = ({ keyInfo, lang, onClick }) => (
    <Tooltip title={keyInfo.name}>
        <div
            onClick={() => onClick && keyInfo.char !== '—' && onClick(keyInfo.char)}
            style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 56,
                margin: 2,
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                backgroundColor: '#fafafa',
                cursor: onClick && keyInfo.char !== '—' ? 'pointer' : 'default',
                boxShadow: '0 2px 0 #d9d9d9',
                transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
                if (onClick && keyInfo.char !== '—') {
                    e.currentTarget.style.backgroundColor = '#e6f7ff';
                }
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fafafa';
            }}
        >
            <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{keyInfo.key}</Text>
            <Text className={lang} style={{ fontSize: 18, fontWeight: 500 }}>{keyInfo.char}</Text>
        </div>
    </Tooltip>
);

const KeyboardRow: React.FC<{
    keys: { key: string; char: string; name: string }[];
    lang: string;
    onClick?: (char: string) => void;
}> = ({ keys, lang, onClick }) => (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
        {keys.map((k, idx) => (
            <KeyButton key={idx} keyInfo={k} lang={lang} onClick={onClick} />
        ))}
    </div>
);

export const KeyboardReference: React.FC<KeyboardReferenceProps> = ({ lang, onClose, onCharacterClick }) => {

    const clickableTagStyle = onCharacterClick ? { margin: 0, cursor: 'pointer' } : { margin: 0 };

    // Style for Hebrew characters in tags - larger font for visibility
    const hebrewCharStyle = { fontSize: 18, fontWeight: 500 };

    // Add dotted circle for combining marks to make them visible
    const formatHebrewChar = (char: string) => {
        // Check if it's a combining character (cantillation/vowel marks are in these ranges)
        const code = char.charCodeAt(0);
        const isCombining = (code >= 0x0591 && code <= 0x05BD) || // Cantillation marks
                           (code >= 0x05BF && code <= 0x05C7) || // More marks
                           (code >= 0x05B0 && code <= 0x05BC);   // Vowel points
        return isCombining ? `◌${char}` : char;
    };

    const greekContent = (
        <div>
            <KeyboardRow keys={greekKeyboard.row1} lang="greek" onClick={onCharacterClick} />
            <KeyboardRow keys={greekKeyboard.row2} lang="greek" onClick={onCharacterClick} />
            <KeyboardRow keys={greekKeyboard.row3} lang="greek" onClick={onCharacterClick} />

            <div style={{ marginTop: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Accents & Breathing Marks:</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {greekModifiers.map((mod, idx) => (
                        <Tooltip key={idx} title={mod.desc}>
                            <Tag
                                style={clickableTagStyle}
                                onClick={() => onCharacterClick && onCharacterClick(mod.result.split(' ')[0])}
                            >
                                <Text code style={{ marginRight: 4 }}>{mod.combo}</Text>
                                <Text className="greek">{mod.result}</Text>
                            </Tag>
                        </Tooltip>
                    ))}
                </div>
            </div>

            <div style={{ marginTop: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Capital Letters (Shift + key):</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {greekCapitals.map((cap, idx) => (
                        <Tooltip key={idx} title={cap.name || cap.char}>
                            <Tag
                                style={{ ...clickableTagStyle, padding: '2px 6px' }}
                                onClick={() => onCharacterClick && onCharacterClick(cap.char)}
                            >
                                <Text code style={{ marginRight: 4, fontSize: 11 }}>{cap.key}</Text>
                                <Text className="greek">{cap.char}</Text>
                            </Tag>
                        </Tooltip>
                    ))}
                </div>
            </div>
        </div>
    );

    const hebrewContent = (
        <div>
            <KeyboardRow keys={hebrewKeyboard.row1} lang="hebrew" onClick={onCharacterClick} />
            <KeyboardRow keys={hebrewKeyboard.row2} lang="hebrew" onClick={onCharacterClick} />
            <KeyboardRow keys={hebrewKeyboard.row3} lang="hebrew" onClick={onCharacterClick} />

            <div style={{ marginTop: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Special Keys:</Text>
                <Space wrap>
                    {hebrewSpecialKeys.map((k, idx) => (
                        <Tooltip key={idx} title={k.name}>
                            <Tag
                                style={clickableTagStyle}
                                onClick={() => onCharacterClick && onCharacterClick(k.char)}
                            >
                                <Text code style={{ marginRight: 8 }}>{k.key}</Text>
                                <Text className="hebrew" style={hebrewCharStyle}>{formatHebrewChar(k.char)}</Text>
                            </Tag>
                        </Tooltip>
                    ))}
                </Space>
            </div>

            <div style={{ marginTop: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Shift Keys (finals, vowels, cantillation):</Text>
                <Space wrap>
                    {hebrewShift.map((f, idx) => (
                        <Tooltip key={idx} title={f.desc}>
                            <Tag
                                style={clickableTagStyle}
                                onClick={() => onCharacterClick && onCharacterClick(f.result)}
                            >
                                <Text code style={{ marginRight: 8 }}>{f.combo}</Text>
                                <Text className="hebrew" style={hebrewCharStyle}>{formatHebrewChar(f.result)}</Text>
                            </Tag>
                        </Tooltip>
                    ))}
                </Space>
            </div>

            <div style={{ marginTop: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Number Keys (cantillation):</Text>
                <Space wrap>
                    {hebrewNumbers.map((item, idx) => (
                        <Tooltip key={idx} title={item.desc}>
                            <Tag
                                style={clickableTagStyle}
                                onClick={() => onCharacterClick && onCharacterClick(item.result)}
                            >
                                <Text code style={{ marginRight: 8 }}>{item.combo}</Text>
                                <Text className="hebrew" style={hebrewCharStyle}>{formatHebrewChar(item.result)}</Text>
                            </Tag>
                        </Tooltip>
                    ))}
                </Space>
            </div>

            <div style={{ marginTop: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Compound Vowels & Cantillation ($X patterns):
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                    Type $ then the key, or click to insert
                </Text>
                <Space wrap>
                    {hebrewAltGr.map((item, idx) => (
                        <Tooltip key={idx} title={item.desc}>
                            <Tag
                                style={clickableTagStyle}
                                onClick={() => onCharacterClick && onCharacterClick(item.result)}
                            >
                                <Text code style={{ marginRight: 8 }}>{item.combo}</Text>
                                <Text className="hebrew" style={hebrewCharStyle}>{formatHebrewChar(item.result)}</Text>
                            </Tag>
                        </Tooltip>
                    ))}
                </Space>
            </div>

            <div style={{ marginTop: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Additional Cantillation (@X patterns):
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                    Type @ then the key, or click to insert
                </Text>
                <Space wrap>
                    {hebrewAltGrShift.map((item, idx) => (
                        <Tooltip key={idx} title={item.desc}>
                            <Tag
                                style={clickableTagStyle}
                                onClick={() => onCharacterClick && onCharacterClick(item.result)}
                            >
                                <Text code style={{ marginRight: 8 }}>{item.combo}</Text>
                                <Text className="hebrew" style={hebrewCharStyle}>{formatHebrewChar(item.result)}</Text>
                            </Tag>
                        </Tooltip>
                    ))}
                </Space>
            </div>
        </div>
    );

    const isGreek = lang === 'greek';
    const languageLabel = isGreek ? 'Greek' : 'Hebrew';

    return (
        <Card
            size="small"
            title={`${languageLabel} Keyboard Reference`}
            extra={onClose && <CloseOutlined onClick={onClose} style={{ cursor: 'pointer' }} />}
            style={{ marginBottom: 16 }}
        >
            {isGreek ? greekContent : hebrewContent}
        </Card>
    );
};

export default KeyboardReference;
